import React, { useState } from "react"
import { Box, Typography } from "@mui/material"
import { prettyFormatDate } from "./util"

const getPlaceholderLabel = (event) => {
  const titleLower = event.name.toLowerCase()
  if (event.category === "mission") {
    if (titleLower.includes("homecoming") || titleLower.includes("home")) {
      return "homecoming"
    }
    return "mission"
  }
  if (event.category === "anniversary") return "anniversary"
  if (event.category === "birthday") {
    const babyNames = ["paul", "jack", "miles", "kenneth", "logan", "connor", "beckham", "jake", "lucy", "dottie", "ford", "adelaide"]
    if (babyNames.some(name => titleLower.includes(name))) {
      return "baby"
    }
    return "portrait"
  }
  return "event"
}

const EventCard = ({ event }) => {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  const placeholderLabel = getPlaceholderLabel(event)
  const formattedDate = prettyFormatDate(event.date)

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: { xs: 2, sm: 3 },
        width: "100%",
      }}
    >
      {/* Thumbnail Container */}
      <Box
        sx={{
          width: 110,
          height: 110,
          minWidth: 110,
          position: "relative",
          borderRadius: "4px",
          overflow: "hidden",
          border: "1px solid #E5DDD3",
          background: "repeating-linear-gradient(-45deg, #FAF6F0, #FAF6F0 5px, #E5DDD3 5px, #E5DDD3 6px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        {/* Actual Image */}
        {!error && event.cardImage && (
          <Box
            component="img"
            src={event.cardImage}
            alt={event.name}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: loaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        )}

        {/* Diagonal Hatch Placeholder Label */}
        {(!loaded || error) && (
          <Typography
            sx={{
              position: "absolute",
              bottom: "6px",
              left: "8px",
              fontFamily: 'Inconsolata, Monaco, Consolas, "Courier New", monospace',
              fontSize: "0.75rem",
              fontWeight: 400,
              color: "#8A7C6E",
              opacity: 0.7,
              textTransform: "lowercase",
            }}
          >
            {placeholderLabel}
          </Typography>
        )}
      </Box>

      {/* Text Content */}
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, pt: 0.5 }}>
        {/* Category & Date Tag */}
        <Typography
          sx={{
            fontFamily: '"Kanit", "Helvetica Neue", Arial, sans-serif',
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            color: "#A65F41", // rust-red terracotta
            textTransform: "uppercase",
            lineHeight: 1.1,
          }}
        >
          {formattedDate} · {event.category}
        </Typography>

        {/* Title */}
        <Typography
          sx={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: { xs: "1.35rem", sm: "1.6rem" },
            fontWeight: 400,
            color: "#2B2621", // dark brown-black
            mt: 0.5,
            mb: 0.5,
            lineHeight: 1.25,
          }}
        >
          {event.name}
        </Typography>

        {/* Description */}
        {event.description && (
          <Typography
            sx={{
              fontFamily: '"Kanit", "Helvetica Neue", Arial, sans-serif',
              fontSize: "0.95rem",
              fontWeight: 300,
              color: "#6E6255", // warm gray-brown
              lineHeight: 1.4,
              mt: 0.5,
            }}
          >
            {event.description}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default EventCard
